import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Popconfirm, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useCrossAccessList, useDeleteCrossAccess } from "../hooks/useCrossAccess";
import { useCrossAccessStore } from "../store/cross-access.store";
import type { OrganizationCrossAccess } from "../types";

interface CrossAccessTableProps {
	page: number;
	limit: number;
	onPageChange: (page: number) => void;
	organizationId?: string;
}

export function CrossAccessTable({
	page,
	limit,
	onPageChange,
	organizationId,
}: CrossAccessTableProps) {
	const { data, isLoading } = useCrossAccessList({ page, limit, organizationId });
	const { mutate: remove } = useDeleteCrossAccess();
	const openEditModal = useCrossAccessStore((s) => s.openEditModal);

	const columns: ColumnsType<OrganizationCrossAccess> = [
		{
			title: "№",
			key: "index",
			width: 60,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		{
			title: "Ko'ruvchi tashkilot",
			key: "viewer",
			ellipsis: true,
			render: (_: unknown, record: OrganizationCrossAccess) => record.viewer?.name ?? "—",
		},
		{
			title: "Tashkilot",
			key: "target",
			ellipsis: true,
			render: (_: unknown, record: OrganizationCrossAccess) => record.target?.name ?? "—",
		},
		{
			title: "Kirish turi",
			dataIndex: "accessType",
			key: "accessType",
			width: 130,
			align: "center",
			render: (type: string) => (
				<Tag color={type === "WRITE" ? "red" : "blue"}>
					{type === "WRITE" ? "Yozish" : "O'qish"}
				</Tag>
			),
		},
		{
			title: "Yaratilgan",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 170,
			render: (date: string) =>
				new Date(date).toLocaleDateString("uz-UZ", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				}),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: OrganizationCrossAccess) => (
				<Dropdown
					menu={{
						items: [
							{
								key: "edit",
								icon: <EditOutlined />,
								label: "Tahrirlash",
								onClick: () => openEditModal(record),
							},
							{
								key: "delete",
								danger: true,
								label: (
									// biome-ignore lint/a11y/useKeyWithClickEvents: ignore
									// biome-ignore lint/a11y/noStaticElementInteractions: ignore
									<div onClick={(e) => e.stopPropagation()}>
										<Popconfirm
											title="O'chirish"
											description="Rostdan ham o'chirmoqchimisiz?"
											onConfirm={() => remove(record.id)}
											okText="Ha"
											cancelText="Yo'q"
											okButtonProps={{ danger: true }}
										>
											<div
												style={{ width: "100%", display: "flex", alignItems: "center", gap: "8px" }}
											>
												<DeleteOutlined />
												<span>O'chirish</span>
											</div>
										</Popconfirm>
									</div>
								),
							},
						],
					}}
					trigger={["click"]}
					placement="bottomRight"
				>
					<Button type="text" icon={<MoreOutlined />} size="small" />
				</Dropdown>
			),
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={data?.data?.items}
			loading={isLoading}
			rowKey="id"
			pagination={tablePagination({
				page,
				limit,
				total: data?.data?.meta?.total,
				onChange: onPageChange,
				sizeChanger: false,
			})}
			size="middle"
		/>
	);
}
